import { useQuery } from '@tanstack/vue-query';
import { useGmApi } from '@/composables/useGmApi';
import { computed, ref, unref } from 'vue';
import type { AcademicYear } from '@/types/academicYears';
import { AcademicYearFactory } from '@/factories/academicYearFactory';
import type { MaybeRefOrGetter } from 'vue';
import { getCurrentAcademicYear } from '@/utils/academicYear';
import { useI18n } from 'vue-i18n';


export function useAcademicYears(useFactoryData = false, enabled: MaybeRefOrGetter<boolean> = true) {
  const { t } = useI18n();
  const { getAcademicYears } = useGmApi();

  // Factory data for development/testing
  const factoryAcademicYears = ref<AcademicYear[]>(
    new AcademicYearFactory().createManySorted(4),
  );

  // Pull queryKey and enabled into dedicated computeds for easier testing
  const queryKeyRef = computed(() => ['academicYears']);
  const queryEnabled = computed(() => unref(enabled) && !useFactoryData);

  const { data: apiAcademicYears, isPending, error } = useQuery({
    queryKey: queryKeyRef.value,
    queryFn: getAcademicYears,
    staleTime: Infinity, // academic years don't change often
    enabled: queryEnabled, // Only fetch from API if enabled and not using factory data
    meta: {
      errorToast: {
        title: t('toast.academicYearsError.title'),
        description: t('toast.genericError.description'),
      },
    },
  });


  // Use factory data or API data based on the flag
  const academicYears = computed(() =>
    useFactoryData ? factoryAcademicYears.value : apiAcademicYears.value,
  );

  const academicYearsMap = computed(() => {
    if (!academicYears.value) return new Map<string, AcademicYear>();

    return new Map(
      academicYears.value.map((year) => [year.uuid, year]),
    );
  });

  // Reverse lookup map: "2024-2025" -> UUID
  const academicYearsReverseMap = computed(() => {
    if (!academicYears.value) return new Map<string, string>();

    return new Map(
      academicYears.value.map((year) => {
        const yearString = `${year.startDate.slice(0, 4)}-${year.endDate.slice(0, 4)}`;
        return [yearString, year.uuid];
      }),
    );
  });

  const academicYearsMapper = (uuid: string, format: 'short' | 'long' = 'long'): string => {
    if (!academicYears.value) return '';

    const academicYear = academicYearsMap.value.get(uuid);
    if (!academicYear) return '';

    // Format: "2024/25"
    if (format === 'short') {
      return `${academicYear.startDate.slice(0, 4)}/${academicYear.endDate.slice(2, 4)}`;
    }

    // Format: "2024/2025"
    return `${academicYear.startDate.slice(0, 4)}/${academicYear.endDate.slice(0, 4)}`;
  };

  const academicYearsMapperWithDashes = (uuid: string): string => {
    if (!academicYears.value) return '';

    const academicYear = academicYearsMap.value.get(uuid);
    if (!academicYear) return '';

    // Format: "2024-2025" to match academicYears array titles
    return `${academicYear.startDate.slice(0, 4)}-${academicYear.endDate.slice(0, 4)}`;
  };

  const findAcademicYearByUuid = (uuid: string): AcademicYear | undefined => {
    return academicYearsMap.value.get(uuid);
  };

  const findAcademicYearByDateRange = (startDate: string, endDate: string): AcademicYear | undefined => {
    if (!academicYears.value) return undefined;

    return academicYears.value.find((year) =>
      year.startDate === startDate && year.endDate === endDate,
    );
  };

  const getAcademicYearUuid = (budget: {
    academic_year_uuid?: string;
    academicYearDateId?: string;
    academicYear?: string;
  }): string | undefined => {
    if (!budget) return undefined;

    // If we have a direct UUID, use it (prioritizing the new academicYearDateId field)
    if (budget.academicYearDateId || budget.academic_year_uuid) {
      return budget.academicYearDateId || budget.academic_year_uuid;
    }

    // If we have a formatted academic year string like "2023-2024",
    // use the reverse lookup map for O(1) performance
    if (budget.academicYear) {
      return academicYearsReverseMap.value.get(budget.academicYear);
    }

    return undefined;
  };

  /**
   * Sorts academic year strings chronologically
   * @param academicYearStrings - Array of academic year strings like ["2025/2026", "2024/2025"]
   * @returns Sorted array like ["2024/2025", "2025/2026"]
   */
  const sortAcademicYearStrings = (academicYearStrings: string[]): string[] => {
    return academicYearStrings.sort((a, b) => {
      // Extract the start year from each string (e.g., "2024" from "2024/2025")
      const startYearA = parseInt(a.split('/')[0]!, 10);
      const startYearB = parseInt(b.split('/')[0]!, 10);
      return startYearA - startYearB;
    });
  };

  const currentAcademicYear = computed(() => {
    if (!academicYears.value) return undefined;
    return getCurrentAcademicYear(academicYears.value) ?? undefined;
  });


  return {
    academicYears,
    academicYearsMap,
    academicYearsReverseMap,
    academicYearsMapper,
    academicYearsMapperWithDashes,
    findAcademicYearByUuid,
    findAcademicYearByDateRange,
    getAcademicYearUuid,
    sortAcademicYearStrings,
    currentAcademicYear,
    // Expose for simpler testing without deep mocking
    queryKeyRef,
    queryEnabled,
    isPending: useFactoryData ? ref(false) : isPending,
    error: useFactoryData ? ref(null) : error,
  };
}
